import { Currency } from "../../models/financial/currency.js";
import { validateAndFormatAddress } from "../../utils/validateAndParseAddress.js";
import invariant from "tiny-invariant";
/**
 * Represents an ERC1155 token with a unique address and some metadata.
 */
export class SemiFungibleToken extends Currency {
    /**
     *
     * @param platformId The platform ID on which this token resides
     * @param address The contract address on the platform on which this token lives
     * @param decimals {@link Currency#decimals}
     * @param symbol {@link Currency#symbol}
     * @param name {@link Currency#name}
     * @param url The URL of the token's metadata
     * @param bypassChecksum If true it only checks for length === 42, startsWith 0x and contains only hex characters
     * @param coinGeckoId CoinGecko ID for price lookups
     */
    constructor(platformId, address, decimals, symbol, name, url, bypassChecksum, coinGeckoId) {
        super(decimals, symbol, name, url, false, true, coinGeckoId);
        this.isNative = false;
        this.isToken = true;
        if (bypassChecksum) {
            const formattedAddress = validateAndFormatAddress(address);
            invariant(formattedAddress, `${address} is not a valid address.`);
            this.address = formattedAddress;
        }
        else {
            const formattedAddress = validateAndFormatAddress(address);
            invariant(formattedAddress, `${address} is not a valid address.`);
            this.address = formattedAddress;
        }
        this.platformId = platformId;
        this.url = url;
    }
    static toSemiFungibleToken(sft) {
        if (!sft) {
            return undefined;
        }
        const currency = Currency.toCurrency(sft.currency);
        return new SemiFungibleToken(sft.platformId ?? sft.chainId, sft.address, currency.decimals, currency.symbol, currency.name, sft.url);
    }
    /**
     * Returns true if the two tokens are equivalent, i.e. have the same platformId and address.
     * @param other other token to compare
     */
    equals(other) {
        // short circuit on reference equality
        if (this === other)
            return true;
        return (this.platformId == other.platformId &&
            validateAndFormatAddress(this.address) == validateAndFormatAddress(other.address));
    }
    /**
     * Returns the currency object that this token represents
     */
    currency() {
        return this;
    }
    /**
     * Converts the semi-fungible token to a model representation for serialization
     */
    toSemiFungibleTokenModel() {
        return {
            __typename: "SemiFungibleToken",
            currency: this.toCurrencyModel(),
            address: validateAndFormatAddress(this.address),
            chainId: this.platformId,
            platformId: this.platformId,
            url: this.url,
        };
    }
    identifier() {
        return `${this.platformId}+${validateAndFormatAddress(this.address)}`;
    }
}
